import React from 'react';
import { AcceptableBatteryIcon, LowBatteryIcon, CriticalBatteryIcon } from '../components/icons/Icons';

/**
 * Normalizes battery level to a percentage (0-100)
 * Handles both decimal (0-1) and percentage (0-100) formats
 * @param {number} batteryLevel - Battery level in decimal (0-1) or percentage (0-100) format
 * @returns {number} Normalized battery level as percentage (0-100), or null if invalid
 */
const normalizeBatteryLevel = (batteryLevel) => {
  if (batteryLevel === null || batteryLevel === undefined || batteryLevel === -1) {
    return null;
  }
  
  // If battery level is <= 1, treat it as decimal (0-1), otherwise as percentage (0-100)
  const isDecimal = batteryLevel <= 1;
  return isDecimal ? Math.round(batteryLevel * 100) : Math.round(batteryLevel);
};

/**
 * Returns the appropriate battery icon component based on battery level
 * Logic: >= 70: AcceptableBatteryIcon, >= 20: LowBatteryIcon, < 20: CriticalBatteryIcon
 * @param {number} batteryLevel - Battery level in decimal (0-1) or percentage (0-100) format
 * @returns {React.Component|null} Battery icon component or null if no battery reading
 */
export const configureBatteryIcon = (batteryLevel) => {
  const normalizedLevel = normalizeBatteryLevel(batteryLevel);
  
  if (normalizedLevel === null) {
    return null;
  }
  
  if (normalizedLevel >= 56) {
    return <AcceptableBatteryIcon />;
  } else if (normalizedLevel >= 25) {
    return <LowBatteryIcon />;
  } else {
    return <CriticalBatteryIcon />;
  }
};

/**
 * Returns the appropriate battery color based on battery level
 * Logic: >= 70: green (#28a745), >= 20: yellow (#ffc107), < 20: red (#dc3545)
 * @param {number} batteryLevel - Battery level in decimal (0-1) or percentage (0-100) format
 * @returns {string} Color hex code for the battery level
 */
export const getBatteryColor = (batteryLevel) => {
  const normalizedLevel = normalizeBatteryLevel(batteryLevel);
  
  if (normalizedLevel === null) {
    return '#6c757d'; // Gray for no data
  }
  
  if (normalizedLevel >= 70) {
    return '#28a745'; // Green
  } else if (normalizedLevel >= 20) {
    return '#ffc107'; // Yellow
  } else {
    return '#dc3545'; // Red
  }
};

/**
 * Returns battery status text based on level
 * @param {number} batteryLevel - Battery level in decimal (0-1) or percentage (0-100) format
 * @returns {string} Status text ('Normal', 'OK', 'Low', or 'No Data')
 */
export const getBatteryStatusText = (batteryLevel) => {
  const normalizedLevel = normalizeBatteryLevel(batteryLevel);
  
  if (normalizedLevel === null) {
    return 'No Data';
  }
  
  if (normalizedLevel >= 70) {
    return 'Normal';
  } else if (normalizedLevel >= 20) {
    return 'OK';
  } else {
    return 'Low';
  }
};

/**
 * Returns battery status string for BatteryCard component
 * Maps battery level to "Good", "Low", or "Empty"
 * @param {number} batteryLevel - Battery level in decimal (0-1) or percentage (0-100) format
 * @returns {string} Status string ('Good', 'Low', 'Empty', or 'Inactive')
 */
export const getBatteryStatusAndColorForCard = (batteryLevel) => {
  const normalizedLevel = normalizeBatteryLevel(batteryLevel);
  
  if (normalizedLevel === null) {
    return { status: 'Inactive', backgroundColor: '#D6D6D6', textColor: '#000000' };
  }
  
  if (normalizedLevel >= 70) {
    return { status: 'Good', backgroundColor: '#457968', textColor: '#ffffff' };
  } else if (normalizedLevel >= 20) {
    return { status: 'Low', backgroundColor: '#F5C10D', textColor: '#000000' };
  } else {
    return { status: 'Empty', backgroundColor: '#9F1919', textColor: '#ffffff' };
  }
};
